import { UserManager, WebStorageStateStore, User } from 'oidc-client-ts';
import { getConfig } from './config';

let userManager: UserManager | null = null;

export function getUserManager(): UserManager {
  if (userManager) return userManager;
  const cfg = getConfig();
  userManager = new UserManager({
    authority: `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}`,
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: true,
    monitorSession: true,
    extraQueryParams: { identity_provider: 'Google' },
    metadataUrl: `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}/.well-known/openid-configuration`,
  });

  // Log silent-renew failures but don't preemptively redirect. Transient
  // failures (network blips during the refresh_token exchange) would otherwise
  // bounce users unnecessarily. Persistent failures self-heal: the stored
  // access token stays stale, the next fetchWithAuth call sees a null/expired
  // user and triggers signinRedirect through its own path.
  userManager.events.addSilentRenewError((err) => {
    console.warn('Silent renew failed', err);
  });

  return userManager;
}

export async function getCurrentUser(): Promise<User | null> {
  const user = await getUserManager().getUser();
  if (!user || user.expired) return null;
  return user;
}

export async function signIn(): Promise<void> {
  await getUserManager().signinRedirect();
}

export async function handleCallback(): Promise<void> {
  await getUserManager().signinRedirectCallback();
}

export async function fetchWithAuth(input: string, init: RequestInit = {}): Promise<Response> {
  const user = await getCurrentUser();
  if (!user) {
    await signIn();
    throw new Error('Redirecting to sign in');
  }
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${user.access_token}`);
  const cfg = getConfig();
  const url = input.startsWith('http') ? input : `${cfg.apiBaseUrl}${input}`;
  const res = await fetch(url, { ...init, headers });
  if (res.status === 401) {
    await signIn();
    throw new Error('Redirecting to sign in');
  }
  return res;
}
